import type { IconName } from '@/components/calc'

export interface CalcInfo {
  slug: string
  href: string
  titulo: string
  descricao: string
  icon: IconName
  /** Palavras que a busca do catálogo também aceita (sinônimos, gírias, erros comuns). */
  busca?: string[]
  /** Gera documento em vez de só um número. */
  documento?: boolean
}

/**
 * Fonte única do CATÁLOGO PÚBLICO. Alimenta a home, o índice /calculadoras e a busca.
 *
 * Regra: toda ferramenta pública entra aqui. Antes, `comprovante`, `dre` e
 * `pró-labore` viviam em rotas soltas fora de /calculadoras — não apareciam no
 * catálogo, não recebiam link interno, e o comprovante (a página que mais
 * converte) nem estava no menu.
 */
export const CALCULADORAS: CalcInfo[] = [
  {
    slug: 'preco-de-venda',
    href: '/calculadoras/preco-de-venda',
    titulo: 'Preço de venda',
    descricao: 'Por quanto vender pra ter lucro.',
    icon: 'tag',
    busca: ['precificacao', 'precificar', 'quanto cobrar', 'markup'],
  },
  {
    slug: 'limite-faturamento-mei',
    href: '/calculadoras/limite-faturamento-mei',
    titulo: 'Faturamento do MEI',
    descricao: 'Teto de 2026, 2027 (R$ 110 mil) e 2028.',
    icon: 'target',
    busca: ['teto', '81 mil', '81000', '110 mil', '140 mil', 'novo limite', 'desenquadramento', 'estourar'],
  },
  {
    slug: 'perdao-dividas-mei',
    href: '/perdao-dividas-mei',
    titulo: 'Perdão das dívidas',
    descricao: 'Tem direito? O que fazer pra não perder.',
    icon: 'alert',
    busca: ['perdao', 'divida', 'atrasado', 'remissao', 'prospera', 'anistia', 'pgfn', 'regularize'],
  },
  {
    slug: 'custo-funcionario-mei',
    href: '/calculadoras/custo-funcionario-mei',
    titulo: 'Custo de funcionário',
    descricao: 'Quanto custa contratar 1 ou 2 pessoas.',
    icon: 'user',
    busca: ['funcionario', 'empregado', 'contratar', 'folha', 'fgts', 'inss', 'salario'],
  },
  {
    slug: 'margem-de-lucro',
    href: '/calculadoras/margem-de-lucro',
    titulo: 'Margem de lucro',
    descricao: 'Quanto sobra de cada venda.',
    icon: 'trend',
    busca: ['lucro', 'porcentagem', 'percentual'],
  },
  {
    slug: 'ponto-de-equilibrio',
    href: '/calculadoras/ponto-de-equilibrio',
    titulo: 'Ponto de equilíbrio',
    descricao: 'O mínimo pra não ter prejuízo.',
    icon: 'scale',
    busca: ['break even', 'empatar', 'prejuizo'],
  },
  {
    slug: 'preco-por-hora',
    href: '/calculadoras/preco-por-hora',
    titulo: 'Preço por hora',
    descricao: 'Quanto vale a sua hora de trabalho.',
    icon: 'clock',
    busca: ['valor hora', 'hora tecnica', 'freelancer', 'diaria'],
  },
  {
    slug: 'fluxo-de-caixa',
    href: '/calculadoras/fluxo-de-caixa',
    titulo: 'Fluxo de caixa',
    descricao: 'Entradas e saídas do mês.',
    icon: 'wallet',
    busca: ['entrou saiu', 'caixa', 'saldo do mes'],
  },
  {
    slug: 'quanto-sobra-de-verdade',
    href: '/calculadoras/quanto-sobra-de-verdade',
    titulo: 'Quanto sobra de verdade',
    descricao: 'Líquido após taxas de maquininha e apps.',
    icon: 'card',
    busca: ['maquininha', 'taxa', 'ifood', 'marketplace', 'cartao', 'pix'],
  },
  {
    slug: 'quanto-posso-me-pagar',
    href: '/quanto-posso-me-pagar-mei',
    titulo: 'Quanto posso me pagar',
    descricao: 'Sua retirada do mês, já descontando DAS e reserva.',
    icon: 'wallet',
    busca: ['pro-labore', 'pro labore', 'retirada', 'salario', 'me pagar'],
  },
  {
    slug: 'dre-simplificado',
    href: '/dre-simplificado-mei',
    titulo: 'DRE simplificado',
    descricao: 'Receita, despesas por categoria e o lucro do período.',
    icon: 'trend',
    busca: ['dre', 'demonstrativo', 'resultado', 'lucro do periodo'],
  },
  {
    slug: 'das',
    href: '/calculadoras/das',
    titulo: 'Calendário do DAS 2026',
    descricao: 'Vencimentos e valor do DAS por tipo de MEI.',
    icon: 'calendar',
    busca: ['imposto', 'guia', 'boleto', 'vencimento'],
  },
  {
    slug: 'comprovante-de-faturamento',
    href: '/comprovante-de-faturamento-mei',
    titulo: 'Comprovante de faturamento',
    descricao: 'O PDF de renda que banco, crédito e imobiliária pedem.',
    icon: 'download',
    busca: ['comprovante', 'renda', 'emprestimo', 'banco', 'aluguel', 'pronampe', 'declaracao'],
    documento: true,
  },
]

/** Normaliza pra busca: minúsculas, sem acento. */
function normalizar(v: string): string {
  return v
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
}

/** Filtra o catálogo por texto livre (título, descrição e sinônimos). */
export function filtrarCalculadoras(termo: string): CalcInfo[] {
  const q = normalizar(termo)
  if (!q) return CALCULADORAS
  return CALCULADORAS.filter((c) => {
    const alvo = normalizar([c.titulo, c.descricao, ...(c.busca ?? [])].join(' '))
    return q.split(/\s+/).every((parte) => alvo.includes(parte))
  })
}
